// biome-ignore format: historic
// biome-ignore format: historic
// biome-ignore format: historic
import Checkbox from '@components/Checkbox/Checkbox';
import PhoneNumberInput, {
  type OnChangeDetails,
  usePhoneNumberInput,
} from '@components/PhoneNumberInput/PhoneNumberInput';
import { editedCardFieldsContext } from '@context/edited-card-fields-context';
import { PopoverContext } from '@context/popover-context';
import { useUser } from '@context/user-context';
import useNode from '@hooks/use-node';
import useOnce from '@hooks/use-once';
import { getSuggestionsByFieldType } from '@modules/card-helpers';
import { TextField } from '@ui-lib';
import { getCountryCallingCode } from 'libphonenumber-js';
import { useContext, useEffect, useState } from 'react';
import type { CardPhoneNumber, CardPresetPhoneNumber } from 'types';

import LinkStatusContainer from '../LinkStatusContainer/LinkStatusContainer';
import SuggestionsSection, {
  useSuggestionsSection,
} from '../SuggestionsSection/SuggestionsSection';
import styles from './PhoneNumberEditPopover.module.scss';

interface PhoneNumberEditPopoverProps<T> {
  field: T;
  onChange: (field: T) => void;
  linked?: boolean;
  mode: 'create' | 'update' | undefined;
}

function PhoneNumberEditPopover<T extends CardPhoneNumber | CardPresetPhoneNumber>({
  field,
  onChange,
  linked = false,
  mode,
}: PhoneNumberEditPopoverProps<T>): React.ReactElement {
  const [valueInputNode, valueInputRef] = useNode<HTMLInputElement>();
  const { show } = useContext(PopoverContext);
  const { user } = useUser();
  const [isFormattedAsInternationalNumber, setIsFormattedAsInternationalNumber] = useState(
    () => field.value.startsWith('+') || mode === 'create',
  );
  const { onEditAttribute, editedAttributes } = useContext(editedCardFieldsContext);
  const options = getSuggestionsByFieldType('phone')();
  const [selectedOption, setSelectedOption] = useSuggestionsSection(field.label, options);
  const hasBeenEdited = editedAttributes.includes(field.id);
  const { countryCode, setCountryCode, phoneNumber, setPhoneNumber } = usePhoneNumberInput(
    field.value,
    field.countryCode ?? user?.country,
    hasBeenEdited,
  );

  const unlock = useOnce(
    (preventSecondRun) => {
      if (show && !field.value.startsWith('+') && mode !== 'create') {
        setIsFormattedAsInternationalNumber(false);
        preventSecondRun();
      }
    },
    [show, field.value],
  );

  useEffect(() => {
    if (!show) unlock();
  }, [show, unlock]);

  useEffect(() => {
    if (show && valueInputNode) {
      const intersectionObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            valueInputNode.focus();
          }, 150);
          observer.unobserve(entry.target);
        }
      });

      intersectionObserver.observe(valueInputNode);
    }
  }, [show, valueInputNode]);

  function handleLabelChange(label: string) {
    onChange({ ...field, label });
  }

  function handleIntlPhoneNumberChange(details: OnChangeDetails) {
    setPhoneNumber(details.number);
    setCountryCode(details.countryCode);
    onEditAttribute(field.id);
    onChange({
      ...field,
      value: details.preservedNumber,
      countryCode: details.countryCode,
    });
  }

  function handleLocalisedPhoneNumberChange(number: string) {
    onEditAttribute(field.id);
    setPhoneNumber(number);
    onChange({
      ...field,
      value: number,
    });
  }

  function handlePhoneNumberFormatChange(isInternational: boolean) {
    if (!isInternational) {
      handleLocalisedPhoneNumberChange(phoneNumber);
    } else {
      const countryCallingCode = getCountryCallingCode(countryCode || 'US');
      onEditAttribute(field.id);
      onChange({
        ...field,
        value: `+${countryCallingCode}${phoneNumber}`,
        countryCode: countryCode || 'US',
      });
    }
    setIsFormattedAsInternationalNumber(isInternational);
  }

  function handleExtensionChange(extension: string) {
    onChange({ ...field, extension });
  }

  function handleSelectSuggestion(suggestion: string) {
    const newSelectedOption = suggestion === selectedOption ? '' : suggestion;

    setSelectedOption(newSelectedOption);
    onChange({ ...field, label: newSelectedOption });
  }

  return (
    <div className={styles['link-fields-edit-popover']}>
      <div className={styles['card-edit-input-group']}>
        <div className={styles['phone-number-input']}>
          <LinkStatusContainer linked={linked} top={-6}>
            {isFormattedAsInternationalNumber && (
              <PhoneNumberInput
                phoneNumber={phoneNumber}
                countryCode={countryCode}
                onChange={handleIntlPhoneNumberChange}
                ref={valueInputRef}
              />
            )}

            {!isFormattedAsInternationalNumber && (
              <input
                type="tel"
                autoComplete="tel-national"
                className={styles['local-phone-number']}
                value={phoneNumber}
                onChange={(event) => handleLocalisedPhoneNumberChange(event.target.value)}
              />
            )}
          </LinkStatusContainer>
        </div>
        <div className={styles['extension-input']}>
          <TextField
            value={field.extension || ''}
            onChange={(event) => handleExtensionChange(event.currentTarget.value)}
            label="Ext."
          />
        </div>
      </div>
      <div className={styles['phone-number-format-selector-container']}>
        <Checkbox
          onChange={handlePhoneNumberFormatChange}
          checked={isFormattedAsInternationalNumber}
          label="Use international number"
          size="small"
          type="subtle"
        />
      </div>

      <div className={styles['label-input']}>
        <TextField
          value={field.label}
          onChange={(event) => handleLabelChange(event.currentTarget.value)}
          label="Label (Optional)"
        />
      </div>

      <div className={styles['suggestions-section-container']}>
        <SuggestionsSection
          options={options}
          onSelect={handleSelectSuggestion}
          selectedOption={selectedOption}
          subject="label"
        />
      </div>
    </div>
  );
}

export default PhoneNumberEditPopover;
